import React, { memo, useEffect, useState, useRef } from "react";
import { Box } from "@mui/material";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import { useIntl } from "react-intl";
import useGetNFTHoldingTable from "../../hooks/useGetNFTHoldingTable";
import _ from "lodash";
import { apiUrl } from "features/configure";
import axios from "axios";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useOutletContext } from "react-router";

const NFTHoldingTable = ({
  walletsIdArray,
  selectedChainIds,
  isUserOnchainDataLoading,
}) => {
  const { formatMessage } = useIntl();
  const dispatchSnackbar = useDispatchSnackbar();
  const [entityName] = useOutletContext();

  const [nftHoldingListFilter, setNFTHoldingListFilter] = useState({});
  const [nftHoldings, setNftHoldings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const prevPageRef = useRef(nftHoldingListFilter.page || 1);
  const parsedWalletsIdArray = JSON.parse(walletsIdArray);
  const rowsPerPage = 5;

  async function getNFTHoldings() {
    try {
      setIsLoading(true);
      const result = await axios.post(
        `${apiUrl}/api/user/${entityName}/nftHoldings`,
        {
          wallets: JSON.parse(walletsIdArray),
          filter: {
            ...nftHoldingListFilter,
            chainIds: selectedChainIds,
          },
          limit: rowsPerPage,
        }
      );
      setTotal(parseInt(_.get(result, "headers.x-total-count", 0)));
      setNftHoldings(result.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (parsedWalletsIdArray?.length === 0 || selectedChainIds?.length === 0) {
      setIsLoading(false);
      return;
    }

    const currentPage = nftHoldingListFilter.page || 1;

    // if page is not changed, means need to reset the filter to page 1
    if (currentPage === prevPageRef.current && currentPage !== 1) {
      setNFTHoldingListFilter((prev) => ({ ...prev, page: 1 }));
    } else {
      getNFTHoldings();
    }

    prevPageRef.current = currentPage;
  }, [walletsIdArray, selectedChainIds, nftHoldingListFilter]);

  const { headers, renderContents } = useGetNFTHoldingTable();

  const handleRequestSort = (event, property) => {
    const { sort, order } = nftHoldingListFilter.orderData || {};
    const isAsc = sort === property && order === 1;
    setNFTHoldingListFilter({
      ...nftHoldingListFilter,
      page: 1,
      orderData: {
        sort: property,
        order: isAsc ? -1 : 1,
      },
    });
  };

  return (
    <Box className="general-shadow">
      <CRMTable
        rowData={nftHoldings || []}
        headers={headers}
        contents={
          parsedWalletsIdArray.length === 0
            ? []
            : _.map(nftHoldings, (nftData, i) => renderContents(nftData))
        }
        isLoading={isLoading || isUserOnchainDataLoading}
        loadingIndicatorAmount={rowsPerPage}
        rowsPerPage={rowsPerPage}
        noDataTitle={formatMessage({
          id: "onchainData.noData.nftHolding.title",
        })}
        page={nftHoldingListFilter.page || 1}
        setPage={(page) =>
          setNFTHoldingListFilter({ ...nftHoldingListFilter, page })
        }
        pageText={formatMessage({ id: "global.pagination.page" })}
        pageOfText={formatMessage({ id: "global.pagination.of" })}
        orderBy={nftHoldingListFilter?.orderData?.sort || ""}
        order={nftHoldingListFilter?.orderData?.order === 1 ? "asc" : "desc"}
        onRequestSort={handleRequestSort}
        dataCounts={total}
      />
    </Box>
  );
};

export default memo(NFTHoldingTable);
